import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import styled from "styled-components";
import { toast } from "react-toastify";
import { Eye } from "lucide-react";
import { removeFromCart, updateQuantity, setCartOpen, updateCartItemVariant } from "@/redux/reducers/cartReducer";

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 998;
  opacity: ${({ open }) => (open ? 1 : 0)};
  visibility: ${({ open }) => (open ? "visible" : "hidden")};
  transition: opacity 0.3s ease, visibility 0.3s ease;
`;

const CartSidebar = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  width: 420px;
  height: 100vh;
  background: #0d1220;
  border-left: 1px solid #1f2937;
  transform: ${({ open }) => (open ? "translateX(0)" : "translateX(110%)")};
  transition: 0.35s ease;
  padding: 24px;
  padding-top: 20px;
  z-index: 999;
  display: flex;
  flex-direction: column;
`;

const ProductImageStyled = styled.img`
  width: 60px;
  height: 60px;
  object-fit: cover;
  border-radius: 8px;
  border: 1px solid #1f2937;
  flex-shrink: 0;
  background: #1f2937;
`;

const PlaceholderImage = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 8px;
  border: 1px solid #1f2937;
  background: #1f2937;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  color: #6b7280;
  flex-shrink: 0;
`;

const ProductImage = ({ src, alt }) => {
  const [hasError, setHasError] = React.useState(false);
  
  if (hasError || !src) {
    return <PlaceholderImage>No Image</PlaceholderImage>;
  }
  
  return (
    <ProductImageStyled 
      src={src} 
      alt={alt} 
      onError={() => setHasError(true)}
    />
  );
};

const MiniChartBar = styled.div`
  height: 8px;
  border-radius: 999px;
  background: linear-gradient(90deg, #00f7d2, #5ce9ff);
  transition: width 0.25s ease-out;
`;

const VariantSelect = styled.select`
  width: 100%;
  margin-top: 8px;
  padding: 8px 10px;
  font-size: 13px;
  background: #0d1220;
  border: 1px solid #1f2937;
  border-radius: 8px;
  color: #e5e7eb;
  cursor: pointer;
  &:focus {
    outline: none;
    border-color: #00f7d2;
  }
`;

const VariantOption = styled.option`
  background: #0d1220;
  color: #e5e7eb;
`;

const VariantLabel = styled.p`
  margin: 0 0 4px 0;
  font-size: 12px;
  color: #9ca3af;
`;

function CartReal({ onCheckout }) {
  const dispatch = useDispatch();
  const history = useHistory();
  const cart = useSelector((state) => state.cart.items);
  const open = useSelector((state) => state.cart.isOpen);
  const [qtyHistory, setQtyHistory] = useState([]);

  useEffect(() => {
    const totalQty = cart.reduce((acc, it) => acc + it.qty, 0);
    if (totalQty === 0) {
      setQtyHistory([]);
    } else {
      setQtyHistory((h) => [...h.slice(-8), totalQty]);
    }
  }, [cart]);

  // Default items with variants but no selected variant to 0-index variant
  useEffect(() => {
    cart.forEach((item, i) => {
      const variants = item.variants || [];
      if (variants.length > 0 && !item.variantId && !item.variant) {
        dispatch(updateCartItemVariant(i, variants[0]));
      }
    });
  }, [cart, dispatch]);

  const handleViewProduct = (productId) => {
    dispatch(setCartOpen(false));
    history.push(`/productDetails?id=${productId}`);
  };

  const updateQty = (index, delta) => {
    const item = cart[index];
    const newQty = item.qty + delta;
    const maxQty = item.availableQty || 999;
    
    if (newQty > maxQty) {
      toast.warning(`Only ${maxQty} items available in stock`);
      return;
    }
    if (newQty < 1) return;
    
    dispatch(updateQuantity(index, delta));
  };

  const removeItem = (index) => {
    dispatch(removeFromCart(index));
  };

  const handleVariantChange = (itemIndex, variant) => {
    if (variant) dispatch(updateCartItemVariant(itemIndex, variant));
  };

  const handleClose = () => {
    dispatch(setCartOpen(false));
  };

  const subtotal = cart.reduce((acc, item) => acc + item.price * item.qty, 0);
  const shipping = cart.reduce((acc, item) => acc + Number(item.deliveryCharges ?? 0), 0);

  const total = subtotal + shipping;
  const maxQtyHistory = qtyHistory.length ? Math.max(...qtyHistory) : 0;

  return (
    <>
      <Overlay open={open} onClick={handleClose} />
      <CartSidebar open={open}>
        <h3>Cart</h3>
      {cart.length === 0 && <p style={{ opacity: 0.7 }}>Your cart is empty.</p>}

      <div style={{ flex: 1, overflowY: "auto" }}>
        {cart.map((item, i) => {
          const variants = item.variants || [];
          const hasVariants = variants.length > 0;
          const selectedVariantIndex = hasVariants
            ? Math.max(0, variants.findIndex((v) => (v._id || v.id) === (item.variantId || item.variant?._id)))
            : 0;
          const effectiveVariant = hasVariants ? (variants[selectedVariantIndex] ?? variants[0]) : null;
          const lotProduction = effectiveVariant?.lot_number && Array.isArray(effectiveVariant.production)
            ? effectiveVariant.production[0]
            : null;

          const effectiveVariantQty = effectiveVariant?.lot_number
            ? (lotProduction?.quantity ?? effectiveVariant.quantity ?? 0)
            : (effectiveVariant?.quantity ?? 0);

          const effectiveVariantPrice = effectiveVariant?.lot_number
            ? (lotProduction?.price ?? effectiveVariant.price ?? 0)
            : (effectiveVariant?.price ?? 0);

          const maxQty = item.availableQty ?? effectiveVariantQty ?? 999;
          const isAtMaxQty = item.qty >= maxQty;
          const displayPrice = item.price ?? effectiveVariantPrice ?? 0;

          return (
            <div
              key={i}
              style={{
                borderBottom: "1px solid #1f2937",
                padding: "12px 0",
                fontSize: 14,
              }}
            >
              <div style={{ display: "flex", gap: "12px", marginBottom: "10px" }}>
                <ProductImage src={item.image} alt={item.name} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <strong style={{ display: "block", marginBottom: "4px", wordBreak: "break-word" }}>
                    {item.name}
                  </strong>
                  <p style={{ margin: 0, color: "#00f7d2", fontWeight: 600 }}>
                    ${Number(displayPrice).toFixed(2)}
                  </p>
                  <p style={{ margin: "4px 0 0", fontSize: "12px", color: isAtMaxQty ? "#f59e0b" : "#6b7280" }}>
                    {isAtMaxQty ? `Max qty reached (${maxQty} available)` : `${maxQty} available`}
                  </p>
                </div>
              </div>

              {hasVariants && (
                <div style={{ marginBottom: "10px" }}>
                  <VariantLabel>Variant</VariantLabel>
                  <VariantSelect
                    value={selectedVariantIndex < 0 ? 0 : selectedVariantIndex}
                    onChange={(e) => {
                      const idx = parseInt(e.target.value, 10);
                      const v = variants[idx];
                      if (v) handleVariantChange(i, v);
                    }}
                  >
                    {variants.map((v, idx) => (
                      <VariantOption key={v._id || v.id || idx} value={idx}>
                        {v.lot_number
                          ? `Lot #${v.lot_number}`
                          : (v.color || v.year || `Option ${idx + 1}`)}
                        {" — "}
                        ${Number(
                          v.lot_number && Array.isArray(v.production)
                            ? v.production[0]?.price ?? v.price ?? 0
                            : v.price ?? 0
                        ).toFixed(2)}
                        {" ("}
                        {v.lot_number && Array.isArray(v.production)
                          ? v.production[0]?.quantity ?? v.quantity ?? 0
                          : v.quantity ?? 0}
                        {" available)"}
                      </VariantOption>
                    ))}
                  </VariantSelect>
                </div>
              )}

              <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                <button 
                  onClick={() => updateQty(i, -1)} 
                  disabled={item.qty <= 1}
                  style={{ 
                    background: '#0d1220', 
                    border: '1px solid #1f2937', 
                    color: item.qty <= 1 ? '#4b5563' : '#e5e7eb', 
                    padding: '4px 8px', 
                    borderRadius: 6, 
                    cursor: item.qty <= 1 ? 'not-allowed' : 'pointer' 
                  }}
                >
                  -
                </button>
                <span style={{ minWidth: "20px", textAlign: "center" }}>{item.qty}</span>
                <button 
                  onClick={() => updateQty(i, +1)} 
                  disabled={isAtMaxQty}
                  style={{ 
                    background: '#0d1220', 
                    border: '1px solid #1f2937', 
                    color: isAtMaxQty ? '#4b5563' : '#e5e7eb', 
                    padding: '4px 8px', 
                    borderRadius: 6, 
                    cursor: isAtMaxQty ? 'not-allowed' : 'pointer' 
                  }}
                >
                  +
                </button>
                <button
                  onClick={() => handleViewProduct(item.productId)}
                  style={{ marginLeft: "auto", background: '#0d1220', border: '1px solid #1f2937', color: '#00f7d2', padding: '4px 8px', borderRadius: 6, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}
                  title="View product"
                >
                  <Eye size={14} />
                  View
                </button>
                <button
                  onClick={() => removeItem(i)}
                  style={{ background: '#0d1220', border: '1px solid #1f2937', color: '#ef4444', padding: '4px 8px', borderRadius: 6, cursor: 'pointer' }}
                >
                  Remove
                </button>
              </div>
            </div>
          );
        })}
      </div>

      <div style={{ marginTop: 10 }}>
        <p style={{ fontSize: 13, opacity: 0.7 }}>Cart activity (qty over time)</p>
        <div
          style={{
            display: "flex",
            gap: 4,
            alignItems: "flex-end",
            height: 32,
            marginBottom: 8,
          }}
        >
          {qtyHistory.map((v, idx) => (
            <div
              key={idx}
              style={{
                width: 10,
                height: maxQtyHistory ? (v / maxQtyHistory) * 30 : 0,
                borderRadius: 999,
                background: "linear-gradient(180deg,#5ce9ff,#00f7d2)",
              }}
            />
          ))}
        </div>
      </div>

      <div style={{ marginTop: "8px" }}>
        <p>Subtotal: ${subtotal.toFixed(2)}</p>
        <p>Shipping: ${shipping.toFixed(2)}</p>
        {/* {discount > 0 && (
          <p style={{ color: "#00f7d2" }}>
            Discount: -{(discount * 100).toFixed(0)}%
          </p>
        )} */}
        <h3>Total: ${total.toFixed(2)}</h3>
      </div>

      <button
        style={{
          width: "100%",
          padding: "12px",
          marginTop: "12px",
          background: "#00f7d2",
          color: "#000",
          fontWeight: 700,
          borderRadius: "10px",
          border: "none",
          cursor: "pointer",
        }}
        onClick={() => {
          handleClose();
          onCheckout();
        }}
      >
        Proceed to Checkout
      </button>

      <button
        style={{ width: "100%", padding: "10px", marginTop: "10px", background: '#0d1220', border: '1px solid #1f2937', color: '#e5e7eb', borderRadius: 10, cursor: 'pointer' }}
        onClick={handleClose}
      >
        Close
      </button>
      </CartSidebar>
    </>
  );
}

export default CartReal;

